import { useMemo } from 'react';
import { css } from '@emotion/react';
import { RRule } from 'rrule';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { FormRow, InlineNotification, Text, TextField } from '@frontend/design-system';
import { AvailabilityScheduleForm } from '../../../../components/availability/availability-form/molecules/availability-schedule-form';
import { useGetLocationOperatoryProviderList } from '../../../../hooks/scheduler-v3/use-get-location-operatory-provider-list';
import { useRuleRecurrenceForm } from '../../../../hooks/settings/use-rule-recurrence-form';
import { useVisibleEntityList } from '../../../../hooks/use-visible-entity-list';
import { getIsPastException } from '../helpers';
import {
  GetRuleExceptionFormFieldProps,
  RuleExceptionFormConfig,
  RuleExceptionFormValues,
} from '../hooks/use-create-edit-rule-exception-form';
import { ExceptionTargetForm } from './exception-target-form';

type Props = {
  locationId: string;
  getFieldProps: GetRuleExceptionFormFieldProps;
  values: RuleExceptionFormValues;
  handleTargetChange: (newTarget: RuleExceptionFormConfig['target']['value']) => void;
  rRule: RRule | undefined;
  handleRRuleChange: (rrule: RRule | undefined) => void;
  isPastOccurrence: boolean;
};

export const AddEditRuleExceptionsForm = ({
  locationId,
  getFieldProps,
  values,
  handleTargetChange,
  rRule,
  handleRRuleChange,
  isPastOccurrence,
}: Props) => {
  const { t } = useTranslation('schedule-settings');

  const serviceTypesQuery = SchedulerV3.Helpers.useGetServiceTypesV3Data({
    selectedLocationId: locationId,
  });

  const serviceList = useMemo(() => {
    return (serviceTypesQuery.data?.appointmentTypeGroups || [])
      .flatMap((group) => group.bookableAppointmentTypes ?? [])
      .map((service) => ({ value: service.id, label: service.displayName }));
  }, [serviceTypesQuery.data?.appointmentTypeGroups]);

  const { providerList, operatoryList } = useGetLocationOperatoryProviderList({
    locationIds: locationId ? [locationId] : [],
  });

  const filteredProviderList = useVisibleEntityList(providerList, [...(values.providers ?? []), values.provider]);
  const filteredOperatoryList = useVisibleEntityList(operatoryList, [...(values.operatories ?? []), values.operatory]);

  const ruleRecurrenceFormProps = useRuleRecurrenceForm({
    locationId,
    values,
    getAvailabilityRuleFieldProps: getFieldProps,
    rRule,
    handleRRuleChange,
  });

  const isPastException = useMemo(() => {
    return getIsPastException({
      isAllDay: values.isAllDay,
      isRecurring: values.isRecurring,
      startDate: values.startDate,
      endDate: values.endDate,
      startTime: values.startTime,
      endTime: values.endTime,
    });
  }, [values.isAllDay, values.isRecurring, values.startDate, values.endDate, values.startTime, values.endTime]);

  return (
    <div css={formContainerStyles}>
      <fieldset disabled={isPastOccurrence} css={fieldsetStyles(isPastOccurrence)}>
        <FormRow>
          <TextField
            {...getFieldProps('name')}
            label={''}
            aria-label={t('Name of Exception')}
            placeholder={t('Name of Exception*')}
          />
        </FormRow>
        <AvailabilityScheduleForm
          locationId={locationId}
          formProps={ruleRecurrenceFormProps}
          isRecurring={!!values.isRecurring}
          switchLabel={t('Repeat This Exception')}
          switchFieldProps={getFieldProps('isRecurring')}
          allDayFieldProps={getFieldProps('isAllDay')}
        />
        <ExceptionTargetForm
          providerList={filteredProviderList}
          operatoryList={filteredOperatoryList}
          serviceList={serviceList}
          getFieldProps={getFieldProps}
          handleTargetChange={handleTargetChange}
        />
      </fieldset>
      {isPastException && (
        <InlineNotification status='info' maxWidth={'100%'} css={{ '& > div': { width: '100%' } }}>
          <Text size='medium'>{t('The selected date and time are in the past.')}</Text>
        </InlineNotification>
      )}
    </div>
  );
};

const formContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  gap: 'var(--fabric-space-gap-lg)',
  height: '100%',
});

const fieldsetStyles = (isPastOccurrence: boolean) =>
  css({
    border: 0,
    padding: 0,
    margin: 0,
    minInlineSize: 0,
    opacity: isPastOccurrence ? 0.5 : 1,
  });
